import * as THREE from "three";

// Tracers are short traveling segments, NOT a static line from gun to target.
// A 25m bright streak travels at 600 m/s along the ray — gives a "spark
// crossing the air" look rather than a thread anchored to the aircraft pod.

const POOL_SIZE = 60;
const SEGMENT_LENGTH = 25;  // metres of visible streak
const SPEED = 600;          // m/s

type Tracer = {
  line: THREE.Line;
  mat: THREE.LineBasicMaterial;
  pos: Float32Array;
  origin: THREE.Vector3;
  dir: THREE.Vector3;
  totalDist: number;
  traveled: number;
  active: boolean;
};

type TracerOptions = {
  color?: number;
  opacity?: number;
};

export class TracerManager {
  private readonly pool: Tracer[] = [];

  constructor(private readonly scene: THREE.Scene) {
    for (let i = 0; i < POOL_SIZE; i++) {
      const pos = new Float32Array(6);
      const geom = new THREE.BufferGeometry();
      geom.setAttribute("position", new THREE.BufferAttribute(pos, 3));
      const mat = new THREE.LineBasicMaterial({
        color: 0xfff0b8,
        transparent: true,
        opacity: 0.98,
        blending: THREE.AdditiveBlending,
        depthWrite: false,
      });
      const line = new THREE.Line(geom, mat);
      line.userData["isVFX"] = true;
      line.visible = false;
      line.frustumCulled = false;
      this.scene.add(line);
      this.pool.push({
        line, mat, pos,
        origin: new THREE.Vector3(),
        dir: new THREE.Vector3(),
        totalDist: 0, traveled: 0,
        active: false,
      });
    }
  }

  add(from: THREE.Vector3, to: THREE.Vector3, options: TracerOptions = {}): void {
    const slot = this.pool.find((t) => !t.active);
    if (!slot) return;
    slot.origin.copy(from);
    slot.dir.subVectors(to, from);
    slot.totalDist = slot.dir.length();
    if (slot.totalDist < 0.1) return;
    slot.dir.normalize();
    slot.traveled = 0;
    slot.active = true;
    slot.line.visible = true;
    slot.mat.color.setHex(options.color ?? 0xfff0b8);
    slot.mat.opacity = options.opacity ?? 1;
  }

  update(dt: number): void {
    for (const t of this.pool) {
      if (!t.active) continue;

      t.traveled += SPEED * dt;

      const tailDist = Math.max(0, t.traveled - SEGMENT_LENGTH);
      const headDist = Math.min(t.traveled, t.totalDist);

      t.pos[0] = t.origin.x + t.dir.x * tailDist;
      t.pos[1] = t.origin.y + t.dir.y * tailDist;
      t.pos[2] = t.origin.z + t.dir.z * tailDist;
      t.pos[3] = t.origin.x + t.dir.x * headDist;
      t.pos[4] = t.origin.y + t.dir.y * headDist;
      t.pos[5] = t.origin.z + t.dir.z * headDist;

      (t.line.geometry as THREE.BufferGeometry).attributes["position"].needsUpdate = true;

      if (tailDist >= t.totalDist) {
        t.active = false;
        t.line.visible = false;
      } else {
        t.mat.opacity = 1.0 * (1 - Math.min(t.traveled / t.totalDist, 1) * 0.28);
      }
    }
  }

  dispose(): void {
    for (const t of this.pool) {
      this.scene.remove(t.line);
      t.line.geometry.dispose();
      t.mat.dispose();
    }
  }
}
